import React, { useCallback, useEffect, useState } from "react";
import {
  Lightbulb,
  Check,
  X,
  AlertTriangle,
  TrendingDown,
  ShieldAlert,
  SearchX,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import { useAuth } from "@/contexts/AuthContext";

type SuggestionTrigger =
  | "coverage_gap"
  | "underperformer"
  | "harmful_replacement";

interface ValidationIssue {
  rule: string;
  message: string;
  blocking: boolean;
}

interface Suggestion {
  id: string;
  name: string;
  category: string;
  text: string;
  totalParameters: number;
  trigger: SuggestionTrigger;
  triggerHistory?: SuggestionTrigger[];
  problemKey?: string;
  evidence?: Record<string, any>;
  rationale?: string;
  replacesTemplateName?: string;
  validationIssues?: ValidationIssue[];
  passedValidation: boolean;
  suggestionCount: number;
  firstSuggestedAt?: string;
  lastSuggestedAt?: string;
  priorityScore: number;
}

const TRIGGER_META: Record<
  SuggestionTrigger,
  { label: string; icon: React.ElementType; className: string; blurb: string }
> = {
  harmful_replacement: {
    label: "Causing opt-outs",
    icon: ShieldAlert,
    className: "bg-red-50 text-red-700 border-red-200",
    blurb: "An existing template is driving people away and needs replacing.",
  },
  coverage_gap: {
    label: "Coverage gap",
    icon: SearchX,
    className: "bg-amber-50 text-amber-700 border-amber-200",
    blurb: "Students keep asking about something no template covers.",
  },
  underperformer: {
    label: "Underperforming",
    icon: TrendingDown,
    className: "bg-blue-50 text-blue-700 border-blue-200",
    blurb: "A template gets far fewer replies than the best performer.",
  },
};

export default function SuggestedTemplates() {
  // Approving a suggestion SUBMITS it to WhatsApp for review, which affects the
  // account's standing in the approval queue — so the backend restricts it to
  // super admins. Mirror that here: a regular admin sees the queue (useful
  // context) but gets no approve/reject buttons, rather than buttons that 403.
  const { isSuperAdmin } = useAuth();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [actioningId, setActioningId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get<Suggestion[]>(
        "/admin/whatsapp/template-suggestions",
      );
      setSuggestions(Array.isArray(resp) ? resp : (resp as any)?.data ?? []);
    } catch (e: any) {
      setError(e?.message || "Could not load suggestions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const review = async (id: string, status: "approved" | "rejected") => {
    const note =
      status === "rejected"
        ? window.prompt("Why are you rejecting this? (helps stop similar suggestions)") ??
          undefined
        : undefined;
    setActioningId(id);
    setError(null);
    try {
      const resp = await api.patch<any>(
        `/admin/whatsapp/template-suggestions/${id}/review`,
        { status, note },
      );
      const r = resp?.data ?? resp;
      setSuggestions((prev) => prev.filter((s) => s.id !== id));
      setNotice(
        status === "approved"
          ? `Submitted to WhatsApp for approval (status: ${r?.providerStatus ?? "pending"}). Review usually takes minutes, but can take up to two days. It becomes usable once a template sync shows it approved.`
          : "Rejected. This problem won't be suggested again.",
      );
    } catch (e: any) {
      setError(e?.message || "Could not save your decision");
    } finally {
      setActioningId(null);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
      <div className="flex items-start justify-between mb-4 gap-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-yellow-100 flex items-center justify-center shrink-0">
            <Lightbulb className="h-5 w-5 text-yellow-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Suggested templates
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Drafted automatically from what the data shows is missing.
              Approving submits the template to WhatsApp for review — it becomes
              usable once approved.
            </p>
          </div>
        </div>

      </div>

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {notice && (
        <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
          {notice}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10">
          <LoadingSpinner />
        </div>
      ) : suggestions.length === 0 ? (
        <div className="text-center py-10">
          <p className="text-sm text-gray-500">
            Nothing suggested right now.
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Suggestions are drafted automatically when the AI can't find a
            template that fits a student — or when one starts causing opt-outs
            or clearly underperforms.
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {suggestions.map((s) => {
            const meta = TRIGGER_META[s.trigger] ?? TRIGGER_META.coverage_gap;
            const Icon = meta.icon;
            const expanded = expandedId === s.id;
            const blocking = (s.validationIssues ?? []).filter((i) => i.blocking);
            const warnings = (s.validationIssues ?? []).filter((i) => !i.blocking);
            const busy = actioningId === s.id;

            return (
              <li
                key={s.id}
                className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
              >
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span
                    className={cn(
                      "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border",
                      meta.className,
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {meta.label}
                  </span>

                  {/* Recurrence is the ranking signal — surface it plainly. */}
                  {s.suggestionCount > 1 && (
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200"
                      title="Times this same problem re-appeared across separate analysis runs"
                    >
                      Seen {s.suggestionCount}×
                    </span>
                  )}

                  {s.replacesTemplateName && (
                    <span className="text-xs text-gray-500">
                      replaces{" "}
                      <code className="bg-gray-100 px-1 py-0.5 rounded">
                        {s.replacesTemplateName}
                      </code>
                    </span>
                  )}

                  {blocking.length > 0 ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      Meta would reject
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                      <Check className="h-3.5 w-3.5" />
                      Passes Meta rules
                    </span>
                  )}
                </div>

                <div className="text-sm font-mono text-gray-500 mb-1">{s.name}</div>

                {/* The message as the student would receive it. */}
                <div className="rounded-md bg-gray-50 border border-gray-200 p-3 text-sm text-gray-800 whitespace-pre-wrap">
                  {s.text}
                </div>

                {s.rationale && (
                  <p className="text-sm text-gray-600 mt-2">{s.rationale}</p>
                )}

                {(blocking.length > 0 || warnings.length > 0) && (
                  <ul className="mt-2 space-y-1">
                    {[...blocking, ...warnings].map((i, idx) => (
                      <li
                        key={idx}
                        className={cn(
                          "text-xs flex items-start gap-1.5",
                          i.blocking ? "text-red-600" : "text-amber-600",
                        )}
                      >
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <span>{i.message}</span>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="flex items-center justify-between mt-3 gap-3">
                  <button
                    onClick={() => setExpandedId(expanded ? null : s.id)}
                    className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
                  >
                    {expanded ? (
                      <ChevronUp className="h-3.5 w-3.5" />
                    ) : (
                      <ChevronDown className="h-3.5 w-3.5" />
                    )}
                    Why this was suggested
                  </button>

                  <div className="flex items-center gap-2">
                    {!isSuperAdmin && (
                      <span className="text-xs text-gray-400">
                        Super admin approval required
                      </span>
                    )}
                    <button
                      onClick={() => review(s.id, "rejected")}
                      disabled={busy || !isSuperAdmin}
                      className={cn(
                        "inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50",
                        !isSuperAdmin && "hidden",
                      )}
                    >
                      <X className="h-4 w-4" />
                      Reject
                    </button>
                    <button
                      onClick={() => review(s.id, "approved")}
                      disabled={busy || blocking.length > 0 || !isSuperAdmin}
                      title={
                        blocking.length > 0
                          ? "WhatsApp would reject this draft — fix or reject it"
                          : "Submits this template to WhatsApp for approval"
                      }
                      className={cn(
                        "inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed",
                        !isSuperAdmin && "hidden",
                      )}
                    >
                      <Check className="h-4 w-4" />
                      {busy ? "Submitting…" : "Approve & submit"}
                    </button>
                  </div>
                </div>

                {expanded && (
                  <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-600 space-y-2">
                    <p className="text-gray-500">{meta.blurb}</p>
                    {s.triggerHistory && s.triggerHistory.length > 1 && (
                      <p>
                        Reached via multiple routes:{" "}
                        {s.triggerHistory.join(", ")} — stronger evidence than a
                        single trigger.
                      </p>
                    )}
                    {s.evidence && (
                      <pre className="bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto text-[11px] leading-relaxed">
                        {JSON.stringify(s.evidence, null, 2)}
                      </pre>
                    )}
                    <p className="text-gray-400">
                      Priority {s.priorityScore}
                      {s.lastSuggestedAt &&
                        ` · last seen ${new Date(s.lastSuggestedAt).toLocaleDateString()}`}
                    </p>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
